import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { withBasePath } from '@/utils/withBasePath';
import type { SavedEventIdsResponse } from '@/pages/api/organizers/[organizerId]/saved-event-ids.page';

// 5m — align with edge cache TTL; balance refetch vs staleness
const STALE_MS = 5 * 60 * 1000;
const MAX_EVENT_IDS_PER_REQUEST = 200;
const EMPTY_SET = new Set<string>();

type SavedEventIdsQueryKey = readonly ['saved-event-ids', string];

type SavedEventIdsCache = {
  savedIds: Set<string>;
  fetchedIds: Set<string>;
};

export type SavedEventIdsLookup = {
  savedEventIds: Set<string>;
  /** True when every visible `eventId` has been checked (or lookup not required). */
  isSaveStateReady: boolean;
};

async function postSavedEventIds(organizerId: string, eventIds: string[]): Promise<Set<string>> {
  const res = await fetch(withBasePath(`/api/organizers/${organizerId}/saved-event-ids`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ eventIds }),
  });
  if (!res.ok) return new Set();
  const data = (await res.json()) as SavedEventIdsResponse;
  return new Set(data.savedEventIds);
}

/** POST in ≤200-id chunks; merges saved ids from every chunk. */
export async function fetchSavedEventIds(
  organizerId: string,
  eventIds: string[],
): Promise<Set<string>> {
  if (eventIds.length === 0) return new Set();
  const merged = new Set<string>();
  for (let i = 0; i < eventIds.length; i += MAX_EVENT_IDS_PER_REQUEST) {
    const batch = eventIds.slice(i, i + MAX_EVENT_IDS_PER_REQUEST);
    const saved = await postSavedEventIds(organizerId, batch);
    for (const id of saved) merged.add(id);
  }
  return merged;
}

async function resolveSavedEventIds(
  organizerId: string,
  visibleEventIds: string[],
  queryClient: QueryClient,
  queryKey: SavedEventIdsQueryKey,
): Promise<SavedEventIdsCache> {
  const prev = queryClient.getQueryData<SavedEventIdsCache>(queryKey);
  const prevSaved = prev?.savedIds ?? new Set<string>();
  const prevFetched = prev?.fetchedIds ?? new Set<string>();
  const toFetch = visibleEventIds.filter((id) => !prevFetched.has(id));
  if (toFetch.length === 0) {
    return { savedIds: prevSaved, fetchedIds: prevFetched };
  }

  const newlySaved = await fetchSavedEventIds(organizerId, toFetch);
  const savedIds = new Set([...prevSaved, ...newlySaved]);
  const fetchedIds = new Set([...prevFetched, ...toFetch]);
  return { savedIds, fetchedIds };
}

function isEveryIdFetched(eventIds: string[], cache: SavedEventIdsCache | undefined): boolean {
  if (eventIds.length === 0) return true;
  if (!cache) return false;
  return eventIds.every((id) => cache.fetchedIds.has(id));
}

export function useSavedEventIds(
  organizerId: string,
  eventIds: string[],
  enabled: boolean,
): SavedEventIdsLookup {
  const queryClient = useQueryClient();
  const queryKey = useMemo(
    () => ['saved-event-ids', organizerId] as SavedEventIdsQueryKey,
    [organizerId],
  );

  // Ref so a deduped/cancelled in-flight fetch always reads the latest visible ids when it runs.
  const eventIdsRef = useRef(eventIds);
  eventIdsRef.current = eventIds;

  const queryFn = useCallback(
    () => resolveSavedEventIds(organizerId, eventIdsRef.current, queryClient, queryKey),
    [organizerId, queryClient, queryKey],
  );

  const queryEnabled = enabled && !!organizerId && eventIds.length > 0;

  const { data: savedEventIds, isFetching } = useQuery({
    queryKey,
    queryFn,
    select: (cache) => cache.savedIds,
    enabled: queryEnabled,
    staleTime: STALE_MS,
  });

  const eventIdsSig = useMemo(() => eventIds.join('\0'), [eventIds]);
  useEffect(() => {
    if (!queryEnabled) return;
    // staleTime: 0 — refetch when paginated ids appear while base query is still fresh
    void queryClient.fetchQuery({ queryKey, queryFn, staleTime: 0 });
    return () => {
      // Drop in-flight work keyed to a stale `eventIds` snapshot so the next effect run
      // cannot join an outdated promise via `fetchQuery` deduplication.
      void queryClient.cancelQueries({ queryKey });
    };
  }, [eventIdsSig, queryEnabled, queryClient, queryKey, queryFn]);

  if (!enabled) {
    return { savedEventIds: EMPTY_SET, isSaveStateReady: true };
  }

  const cache = queryClient.getQueryData<SavedEventIdsCache>(queryKey);
  const isSaveStateReady = isEveryIdFetched(eventIds, cache) && !isFetching;

  return {
    savedEventIds: savedEventIds ?? EMPTY_SET,
    isSaveStateReady,
  };
}
